import React, { useRef, useState } from 'react';
import { useModal } from 'components/modals/ModalContext';
import { EventType, SchedulingView } from './SchedulingTypes';
import { determineStatusColorClass } from './functions/determineStatusColorClass';
import JobModal from '../jobs/JobModal';
import { Draggable } from 'services/dragdrop/Draggable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock } from '@fortawesome/free-solid-svg-icons';
import EditActivityForm from './EditActivityForm';
import EditActivityRecurrenceForm from './EditActivityRecurrenceForm';
import { MIN_EVENT_RENDER_MINUTES, MIN_RESIZE_MINUTES, MIN_RESOURCE_ROW_HEIGHT } from './schedulingConstants';
import { calculateEventLanes } from './functions/calculateOverlappingEvents';
import { useTranslation } from 'react-i18next';
import { truncate } from 'services/utils/truncate';
import { APPOINTMENT_TEXT_SEPARATOR, formatAppointmentText } from './functions/appointmentText';

interface EventProps {
    view: SchedulingView;
    event: EventType,
    colorMethod: string | null,
    currentResource: string | number,
    currentDay: string,
    currentTimeSlot?: string,
    pixelsPerMinute?: number,
    resourceDirection?: string,
    isCompactRow?: boolean,
    dayEndTime?: string,
    overlappingEvents?: [EventType[], number];
    onResize?: (event: EventType, newDurationMinutes: number) => void;
    onOpenRightClickMenu: (menu: any) => void;
    onCloseRightClickMenu?: () => void;
    onShowTooltip: (x: number, y: number, event: EventType) => void;
    onHideTooltip: () => void;
    onRefresh: () => void;
};

const Event: React.FC<EventProps> = ({
    view, event, colorMethod, currentResource, currentDay, currentTimeSlot, pixelsPerMinute,
    resourceDirection, isCompactRow, dayEndTime, overlappingEvents, onResize, onOpenRightClickMenu, onCloseRightClickMenu, onShowTooltip, onHideTooltip, onRefresh
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const [renderOnTimeSlot] = useState<boolean>(!!currentTimeSlot);
    const [resizeDurationMinutes, setResizeDurationMinutes] = useState<number | null>(null);
    const [resizeTooltipPosition, setResizeTooltipPosition] = useState<{ left: number, top: number } | null>(null);
    const eventEntered = useRef(false);
    const eventPosition = useRef<{ x: number, y: number } | null>(null);

    // Calculate the event start time offset in pixels from the start of the timeslot
    const calculateStartOffset = () => {
        // Return if the needed values are missing
        if (!event.startDateTime || !currentTimeSlot || !pixelsPerMinute) return;

        // Get the minutes of the timeslot and the start time (e.g. '45' of '07:45')
        const startTimeString = event.startDateTime.split('T')[1];
        const startTimeMinutes = Number(startTimeString.split(':')[1]);
        const timeSlotStartMinutes = Number(currentTimeSlot.split(':')[1]);
        
        // Calculate the start offset in minutes and pixels
        const offsetMinutes = startTimeMinutes - timeSlotStartMinutes;
        const offsetPixels = offsetMinutes * pixelsPerMinute;

        return offsetPixels;
    };

    // Calculate the event duration in pixels, a running resize overrides the stored duration
    const calculateDurationPixels = () => {
        // Return if the needed values are missing
        const durationMinutes = resizeDurationMinutes ?? event.durationMinutes;
        if (!durationMinutes || !pixelsPerMinute) return;

        // Render the event with a minimum duration so it stays clickable
        const durationPixels = (Math.max(durationMinutes, MIN_EVENT_RENDER_MINUTES) * pixelsPerMinute) - 1;

        // Clamp to a minimum width so tiny events never get an invalid negative width
        return Math.max(durationPixels, 2);
    };

    // Convert a time string like 07:45 to minutes since midnight
    const timeToMinutes = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    // Convert minutes since midnight back to a time string like 07:45
    const minutesToTime = (minutes: number) => {
        const boundedMinutes = ((minutes % 1440) + 1440) % 1440;
        return `${String(Math.floor(boundedMinutes / 60)).padStart(2, '0')}:${String(boundedMinutes % 60).padStart(2, '0')}`;
    };

    // Determine if the event is colored by a running travel or work timer
    const hasRunningTimerColor = (
        event.type === 'appointment' &&
        ['running_travel_timer', 'running_work_timer'].includes(determineStatusColorClass(event))
    );

    // Determine if the event duration can be resized by dragging its edge, using the same
    // rules as dragging so done, locked and running appointments stay fixed
    const isResizable = (
        renderOnTimeSlot &&
        !!pixelsPerMinute &&
        !!onResize &&
        event.type === 'appointment' &&
        event.status !== 'done' &&
        !event.locked &&
        !hasRunningTimerColor
    );

    // Handle the resizing of the event duration by dragging its end edge
    const startResize = (mouseDownEvent: React.MouseEvent) => {
        // Keep the drag system and click handlers away from the resize
        mouseDownEvent.preventDefault();
        mouseDownEvent.stopPropagation();
        if (!pixelsPerMinute || !event.startTime) return;

        // Determine the resize direction and the starting values
        const isHorizontal = resourceDirection === 'stacked';
        const startCoordinate = isHorizontal ? mouseDownEvent.clientX : mouseDownEvent.clientY;
        const originalDuration = event.durationMinutes ?? 60;

        // Get the event element to position the resize tooltip below it
        const eventElement = (mouseDownEvent.currentTarget as HTMLElement).closest('.event') as HTMLElement | null;

        // Determine the maximum duration up to the end of the displayed day
        const maxDuration = dayEndTime ? timeToMinutes(dayEndTime) - timeToMinutes(event.startTime) : null;

        let latestDuration = originalDuration;

        // Resize the event along with the mouse, in steps of a quarter and clamped
        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaPixels = (isHorizontal ? moveEvent.clientX : moveEvent.clientY) - startCoordinate;
            const deltaMinutes = deltaPixels / pixelsPerMinute;

            // Step per quarter from the original duration and clamp between the minimum and the end of the day
            let newDuration = originalDuration + Math.round(deltaMinutes / 15) * 15;
            newDuration = Math.max(MIN_RESIZE_MINUTES, newDuration);
            if (maxDuration && maxDuration >= MIN_RESIZE_MINUTES) {
                newDuration = Math.min(newDuration, maxDuration);
            }

            latestDuration = newDuration;
            setResizeDurationMinutes(newDuration);

            // Position the tooltip with the new times below the event
            if (eventElement) {
                const eventRect = eventElement.getBoundingClientRect();
                setResizeTooltipPosition({ left: eventRect.left, top: eventRect.bottom + 4 });
            }
        };

        // Persist the new duration once the mouse button is released
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            if (latestDuration !== originalDuration) {
                // Suppress the click which follows this mouseup so the job modal stays closed
                const suppressClick = (clickEvent: MouseEvent) => clickEvent.stopPropagation();
                document.addEventListener('click', suppressClick, { capture: true, once: true });

                // Persist the new duration and update the events state
                if (onResize) onResize(event, latestDuration);
            }

            setResizeDurationMinutes(null);
            setResizeTooltipPosition(null);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Sets the absolute position of timeline rendered events
    const timeRenderedPosition = () => {
        // Return if the needed values are missing
        if (!pixelsPerMinute || !event.startDateTime) return {};
    
        // Determine the overlap and width percentage of the event
        const [overlappingEventList, widthPercentage] = overlappingEvents ?? [[], 100];

        // Determine the stack lane of the event, events without visual
        // overlap share the same lane so they stay on one row
        const eventLanes = calculateEventLanes(overlappingEventList);
        const lane = eventLanes.get(event.key) ?? 0;

        // Calculate the positioning values of the appointment
        const startTimeOffset = calculateStartOffset();
        const durationPixels = calculateDurationPixels();
    
        let timeRenderedStyle: React.CSSProperties = {};
    
        // For stacked direction, the appointments are positioned from left to right
        if (resourceDirection === 'stacked') {

            // In compact mode every event gets the same fixed height and is stacked
            // below the events it overlaps with, so a single event on a quiet day
            // stays just as high as an event on a busy day
            if (isCompactRow) {
                const compactStyle: React.CSSProperties = {
                    position: 'absolute',
                    top: `${lane * MIN_RESOURCE_ROW_HEIGHT}px`,
                    left: `${startTimeOffset}px`,
                    width: `${durationPixels}px`,
                    height: `${MIN_RESOURCE_ROW_HEIGHT - 1}px`,
                };

                return compactStyle;
            }

            // Otherwise the events share the height of the row
            timeRenderedStyle = {
                position: 'absolute',
                top: `calc(${(lane * widthPercentage)}% + 1px)`,
                left: `${startTimeOffset}px`,
                width: `${durationPixels}px`,
                height: `calc(${widthPercentage}% - 2px)`,
            };
        }

        // For columns direction, the appointments are positioned from top to bottom
        if (resourceDirection === 'columns') {
            timeRenderedStyle = {
                position: 'absolute',
                top: `${startTimeOffset}px`,
                left: `calc(${(lane * widthPercentage)}% + 1px)`,
                width: `calc(${widthPercentage}% - 2px)`,
                height: `${durationPixels}px`,
            };
        }
    
        return timeRenderedStyle;
    };

    // Determine event classname
    const determineEventClassName = () => {
        let className = 'event';

        // Render the event without a radius and vertically centered
        // when the resource rows fell back to the minimum height
        if (isCompactRow) {
            className += ' compact';
        }

        // Set classname for appointment events
        if (event.type === 'appointment') {

            // If the color method is custom, add custom color if it exist, otherwise fallback to default custom color
            if (colorMethod === 'custom') {
                className += event.custom_color ? ` ${event.custom_color}` : ` default-event-color`;
            }

            // If the color method is based on type, add the color of the job type, otherwise fallback to default color
            else if (colorMethod === 'type') {
                className += event.job?.type_color ? ` ${event.job.type_color}` : ` default-event-color`;
            }

            // If color method is based on status, add the status color class of the event
            else if (event.job) {
                className += ` ${determineStatusColorClass(event)}`;
            }
        }

        // Add the color to timerow events
        if (event.type === 'timerow') {

            // If the color method is based on type, add the color of the job type, otherwise fallback to default color
            if (colorMethod === 'type') {
                className += event.job?.type_color ? ` ${event.job.type_color}` : ` default-event-color`;
            }

            // If color method is based on status, add the status color class of the event
            else {
                className += ` ${determineStatusColorClass(event)}`;
            }
        }

        // Add classname for activity events, marked so their text color can differ from appointments
        if (event.type === 'activity') {
            className += event.custom_color ? ` activity ${event.custom_color}` : ` activity default-activity-event-color`;
        }

        return className;
    }

    // Render event content
    const renderEventContent = () => {
        switch (event.type) {
            case "appointment":
            case "timerow": {
                // The timeline positions the block on its time, the list views show the start time first
                const showStartTime = view.viewType !== 'employee_timeline';

                // Timerows are registered time, so they get the done check like a done appointment
                const showDoneCheck = colorMethod !== 'status' && (event.type === 'timerow' || event.status === 'done');

                // Build the text from the chosen job fields of the appointment text setting
                const appointmentText = formatAppointmentText(event.job, view.appointmentText);

                return (
                    <div className='event-content'>
                        {event.type === 'appointment' && event.locked && (
                            <span className='event-locked-icon'>
                                <FontAwesomeIcon icon={faLock} />
                            </span>
                        )}

                        {showStartTime && (
                            <span className='event-start-time'>{event.startTime}</span>
                        )}

                        {appointmentText && (
                            <span className='event-text'>
                                {showStartTime ? `${APPOINTMENT_TEXT_SEPARATOR}${appointmentText}` : appointmentText}
                            </span>
                        )}

                        {showDoneCheck && (
                            <span style={{ margin: '0 5px' }}>
                                <FontAwesomeIcon icon={faCheck} />
                            </span>
                        )}
                    </div>
                );
            }

            case "activity":
                if (view.viewType === 'employee_timeline') {
                    return (
                        <div className='event-content'>
                            {event.time_type_name && <span>{event.time_type_name}</span>}
                            {event.note && <span> - {truncate(event.note, 40)}</span>}
                        </div>
                    );
                } else {
                    return (
                        <div className='event-content'>
                            <span>{event.startTime}</span>

                            {event.time_type_name && <span> • {event.time_type_name}</span>}
                            {event.note && <span> - {truncate(event.note, 40)}</span>}
                        </div>
                    );
                }

            case "availableEmployee":
                return (
                    <div className='event-content'>
                        {event.employeeName && (
                            <span>{event.employeeName}</span>
                        )}
                    </div>
                );
            
            default:
                return null;

        }
    };
    
    // Handle clicking on the event
    const handleEventClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Close the right click menu, if it is opened
        onCloseRightClickMenu?.();

        // Open job modal for appointment and timerow events
        if (event.type === 'appointment' || event.type === 'timerow') {
            initializeModal(
                React.cloneElement(<JobModal />, { itemId: event.job.id }), { 
                    itemId: event.job.id, 
                    modalSize: 'large',
                    onClose: () => {
                        // Trigger scheduler event refresh in case the job is changed
                        onRefresh();
                    } 
                }
            );
        };

        // Open the recurrence modal for a virtual occurrence of a recurring activity
        if (event.type === 'activity' && event.recurrenceId) {
            initializeModal(
                React.cloneElement(<EditActivityRecurrenceForm />, {
                    itemId: event.recurrenceId,
                    linkedItem: { effective_from: event.occurrenceDate }
                }), {
                    title: t('scheduling.other_activity.edit_activity_label'),
                    itemId: event.recurrenceId,
                    modalSize: 'small',
                    onSuccess: () => {
                        // Trigger scheduler event refresh
                        onRefresh();
                    }
                }
            );

        // Open activity modal for other, non-recurring activities
        } else if (event.type === 'activity') {
            initializeModal(
                React.cloneElement(<EditActivityForm />, { itemId: event.activityId }), {
                    title: t('scheduling.other_activity.edit_activity_label'),
                    itemId: event.activityId,
                    modalSize: 'small',
                    onSuccess: () => {
                        // Trigger scheduler event refresh
                        onRefresh();
                    }
                }
            );
        };
    };

    // Show tooltip on mouse enter
    const handleMouseEnter = (e: React.MouseEvent) => {
        eventEntered.current = true;
        eventPosition.current = { x: e.clientX, y: e.clientY };
        onShowTooltip(e.clientX + 12, e.clientY + 12, event);
    };

    // Show tooltip on mouse mouve
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!eventEntered.current && !eventPosition.current) {
            onShowTooltip(e.clientX + 12, e.clientY + 12, event);
        }
    };

    // Hide tooltip on mouse leave
    const handleMouseLeave = () => {
        eventPosition.current = null;
        onHideTooltip();
    };

    // Handle open right click menu
    const handleOpenRightClickMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        onOpenRightClickMenu({
            type: 'event',
            x: e.clientX,
            y: e.clientY,
            event
        });
    }

    return (
        <Draggable
            key={event.key}
            draggableId={ 
                event.type === 'appointment' 
                    ? { type: 'appointment', event } 
                    : event.type === 'activity' 
                        ? { type: 'activity', event }
                        : null 
            }
            isDragDisabled={
                event.type === "timerow"
                    ? {
                        disabled: true,
                        message: "scheduling.scheduling_board.timerow_event_dragging_disabled",
                    }
                    : event.type === "appointment" && event.status === "done"
                    ? {
                        disabled: true,
                        message: "scheduling.scheduling_board.done_appointment_dragging_disabled",
                    }
                    : event.type === "appointment" && event.locked
                    ? {
                        disabled: true,
                        message: t('scheduling.scheduling_board.locked_appointment_dragging_disabled'),
                    }
                    : event.type === "activity" && event.recurrenceId
                    ? {
                        disabled: true,
                        message: "scheduling.scheduling_board.activity_recurrence_dragging_disabled",
                    }
                    : undefined
            }
            sourceDroppableArea={{ 
                ...(view.viewType === 'employee_timeline' 
                    ? { type: 'timeSlot' } 
                    : { type: 'dayList' }
                ),
                resourceId: currentResource,
                day: currentDay,
                ...(currentTimeSlot ? { timeSlot: currentTimeSlot } : {})
            }}>

            {view.viewType === 'employee_timeline' ? (
                <div className={determineEventClassName()}
                     onClick={handleEventClick}
                     onMouseEnter={handleMouseEnter}
                     onMouseMove={handleMouseMove}
                     onMouseLeave={handleMouseLeave}
                     onContextMenu={handleOpenRightClickMenu}
                     style={renderOnTimeSlot ? timeRenderedPosition() : {}}>
                    {renderEventContent()}
                    {isResizable && (
                        // Handle on the end edge of the event to resize its duration, a plain
                        // click on it bubbles up to the event and opens the job modal
                        <div
                            className={`event-resize-handle ${resourceDirection === 'stacked' ? 'horizontal' : 'vertical'}`}
                            onMouseDown={startResize}
                        />
                    )}
                    {resizeDurationMinutes !== null && resizeTooltipPosition && event.startTime && (
                        // Tooltip below the event with the times while resizing
                        <div className='event-resize-tooltip' style={{ left: resizeTooltipPosition.left, top: resizeTooltipPosition.top }}>
                            {event.startTime} - {minutesToTime(timeToMinutes(event.startTime) + resizeDurationMinutes)}
                        </div>
                    )}
                </div>
            ) : (
                <div className={determineEventClassName()} 
                     onClick={handleEventClick}
                     onMouseEnter={handleMouseEnter}
                     onMouseMove={handleMouseMove}
                     onMouseLeave={handleMouseLeave}
                     onContextMenu={handleOpenRightClickMenu}>
                    <div className='list-event'>
                        {renderEventContent()}
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export default Event;